/** Keep in sync with supabase/functions/_shared/recapScoreImport.ts */
import { recapTeamShortCode } from "./recapSheetModel";
import { sortTeamAssignments } from "../teamAssignments";

const HEX = /[0-9A-F]/g;
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const SKIP_NEXT_STEP = {
  "Could not match this recap to a team.":
    "Confirm the BTA Use code and the squad, then extract again.",
  "This recap matches more than one team.":
    "Confirm the squad and re-print the recap for the team you want.",
  "No game scores were read for this team.":
    "Check the photo and extract again.",
  "Could not read which team this recap belongs to.":
    "Re-print the recap so the BTA Use code is visible, then extract again.",
  "No scores were found for this team.": "Check the photo and extract again.",
  "Could not match a written name to this team.":
    "Confirm the squad and re-print the recap for the team you want.",
  "Could not tell which bowler a score row belongs to.":
    "Check the handwritten names and extract again.",
  "That team has no bowlers.": "Add the bowlers on Scoring, then extract again.",
};

export const normalizeRecapTeamCode = (value) => {
  const text = String(value || "")
    .toUpperCase()
    .replace(/BTA\s*USE\s*:?/g, "")
    .replace(/O/g, "0");
  const hex = (text.match(HEX) || []).join("");
  if (hex.length < 4) return "";
  return hex.slice(0, 8);
};

export const parseRecapGameScore = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    const score = Math.round(value);
    return score >= 0 && score <= 300 ? score : null;
  }
  const text = String(value).trim();
  if (!text) return null;
  const match = text.match(/\d+/);
  if (!match) return null;
  const score = Number(match[0]);
  return Number.isInteger(score) && score >= 0 && score <= 300 ? score : null;
};

const normalizeName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const looseNameMatch = (left, right) => {
  if (!left || !right) return false;
  if (left === right) return true;
  const [shorter, longer] =
    left.length <= right.length ? [left, right] : [right, left];
  if (shorter.length < 3) return false;
  return longer.startsWith(`${shorter} `);
};

export const teamMatchesRecapCode = (teamId, extractedCode) => {
  const printed = recapTeamShortCode(teamId);
  const code = normalizeRecapTeamCode(extractedCode);
  if (!printed || !code) return false;
  return printed === code || printed.startsWith(code) || code.startsWith(printed);
};

export const findTeamForRecapCode = (teams, extractedCode) => {
  const matches = (teams || []).filter((team) =>
    teamMatchesRecapCode(team.id, extractedCode)
  );
  if (matches.length === 1) return { team: matches[0] };
  if (matches.length > 1) {
    return { error: "This recap matches more than one team." };
  }
  return { error: "Could not match this recap to a team." };
};

const bowlerNamesFrom = (rows) =>
  (rows || [])
    .map((row) => String(row?.name || row?.bowlers?.name || "").trim())
    .filter(Boolean);

export const recapSkipLabel = ({ team, block } = {}) => {
  const teamName = String(team?.teamName || team?.name || "").trim();
  if (teamName) return teamName;
  const roster = bowlerNamesFrom(rosterBowlers(team));
  if (roster.length) return roster.join(", ");
  const extracted = bowlerNamesFrom(block?.bowlers);
  if (extracted.length) return extracted.join(", ");
  return "This recap";
};

const rosterBowlers = (team) => {
  const rows = team?.bowlers || team?.teamassignments || [];
  const ordered = rows.some((row) => row?.created)
    ? sortTeamAssignments(rows)
    : rows;
  return ordered
    .map((row) => ({
      id: row.bowlerID || row.bowlers?.id || row.id,
      name: row.name || row.bowlers?.name || "",
    }))
    .filter((row) => row.id);
};

const rosterNames = (team) =>
  rosterBowlers(team)
    .map((row) => normalizeName(row.name))
    .filter(Boolean);

const extractedRows = (block) =>
  (block?.bowlers || []).filter(
    (bowler) =>
      String(bowler?.name || "").trim() ||
      (bowler?.games || []).some((game) => parseRecapGameScore(game) != null)
  );

const namesFitRoster = (extracted, roster) => {
  if (!extracted.length || extracted.length !== roster.length) return false;
  const used = new Set();
  return extracted.every((name) => {
    const hit = roster.findIndex(
      (row, index) => !used.has(index) && looseNameMatch(name, row)
    );
    if (hit < 0) return false;
    used.add(hit);
    return true;
  });
};

const squadOf = (team) => {
  const nested = Array.isArray(team?.squads) ? team.squads[0] : team?.squads;
  return {
    date: team?.squadDate || team?.squad?.squadDate || nested?.squadDate || "",
    time: team?.squadTime || team?.squad?.squadTime || nested?.squadTime || "",
  };
};

const formatRecapClock = (timeValue) => {
  const match = String(timeValue || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  let hour = Number(match[1]);
  const minute = match[2];
  if (!Number.isFinite(hour) || hour > 23) return "";
  const suffix = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return minute === "00" ? `${hour} ${suffix}` : `${hour}:${minute} ${suffix}`;
};

export const formatRecapSquadWhen = (dateValue, timeValue) => {
  const dateText = String(dateValue || "").slice(0, 10);
  const match = dateText.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const month = MONTHS[Number(match[2]) - 1];
  const day = Number(match[3]);
  if (!month || !day) return "";
  const clock = formatRecapClock(timeValue);
  return clock ? `${month} ${day}, ${clock}` : `${month} ${day}`;
};

export const formatRecapLane = (team) => {
  const lane = Number(team?.startingLane);
  if (Number.isFinite(lane) && lane >= 1) return `Lane ${Math.floor(lane)}`;
  const pair = Number(team?.startingPair);
  if (!Number.isFinite(pair) || pair < 1) return "";
  const start = Math.floor(pair) % 2 === 0 ? Math.floor(pair) - 1 : Math.floor(pair);
  return `Pair ${start}-${start + 1}`;
};

export const recapTeamIdentity = (team, code) => {
  if (!team) return "This recap";
  const who =
    String(team.teamName || team.name || "").trim() ||
    bowlerNamesFrom(rosterBowlers(team)).join(", ");
  const squad = squadOf(team);
  const when = formatRecapSquadWhen(squad.date, squad.time);
  const lane = formatRecapLane(team);
  const bta = recapTeamShortCode(team.id) || normalizeRecapTeamCode(code);
  return [who, when, lane, bta ? `BTA Use ${bta}` : ""].filter(Boolean).join(" · ") ||
    "This recap";
};

const codeCandidates = (teams, code) => {
  const exact = (teams || []).filter((team) => recapTeamShortCode(team.id) === code);
  if (exact.length) return exact;
  return (teams || []).filter((team) => teamMatchesRecapCode(team.id, code));
};

const mismatchReason = (team, verified) => {
  if (verified.has(team?.id)) {
    return "Scores on this team are verified, but the names on the sheet do not match this roster. Nothing was imported. Unverify this team only if the sheet is really for them; otherwise print a fresh recap for the team you want.";
  }
  return "The names on this sheet do not match this team. Nothing was imported. Confirm the squad, then re-print or re-upload the recap for the team you want.";
};

const unverifiedTwinCount = (team, teams, verified) => {
  const names = rosterNames(team);
  if (!names.length) return 0;
  return (teams || []).filter((other) => {
    if (!other?.id || other.id === team.id || verified.has(other.id)) return false;
    return namesFitRoster(names, rosterNames(other));
  }).length;
};

export const verifiedRecapReason = (team, teams, verified) => {
  let reason =
    "Scores on this team are verified. Unverify this team on Scoring, then extract again.";
  if (unverifiedTwinCount(team, teams, verified) > 0) {
    reason += " Other unverified cards with these bowlers were left blank.";
  }
  return reason;
};

export const isPlainVerifiedSkip = (row) =>
  /^Scores on this team are verified\./.test(String(row?.reason || "").trim());

export const isVerifiedRecapSkip = (row) =>
  /^Scores on this team are verified\b/.test(String(row?.reason || "").trim());

const teamByCode = (teams, code) => {
  const normalized = normalizeRecapTeamCode(code);
  if (!normalized) return null;
  const hits = codeCandidates(teams, normalized);
  return hits.length === 1 ? hits[0] : null;
};

const appendNextStep = (reason) => {
  const text = String(reason || "").trim();
  const step = SKIP_NEXT_STEP[text];
  if (!step) return text;
  return `${text} ${step}`;
};

export const dedupeRecapSkips = (rows) => {
  const seen = new Set();
  const kept = [];
  (rows || []).forEach((row) => {
    const key = [
      row?.teamId || "",
      row?.teamCode || "",
      row?.label || "",
      row?.reason || "",
    ].join("|");
    if (seen.has(key)) return;
    seen.add(key);
    kept.push(row);
  });
  return kept;
};

export const presentRecapSkips = ({ skipped = [], teams = [] } = {}) => {
  const verified = new Set(
    (teams || []).filter((team) => team?.isVerified && team.id).map((team) => team.id)
  );
  const rows = (skipped || []).map((row) => {
    const team = teamByCode(teams, row?.teamCode);
    if (team?.id && isPlainVerifiedSkip(row)) verified.add(team.id);
    if (team && isPlainVerifiedSkip(row)) {
      return {
        ...row,
        teamId: team.id,
        label: recapTeamIdentity(team, row.teamCode),
        reason: verifiedRecapReason(team, teams, verified),
      };
    }
    if (!team && isPlainVerifiedSkip(row)) {
      return {
        ...row,
        reason: verifiedRecapReason({}, teams, verified),
      };
    }
    if (team) {
      return {
        ...row,
        teamId: team.id || row.teamId || "",
        label: recapTeamIdentity(team, row.teamCode),
        reason: appendNextStep(row.reason),
      };
    }
    return {
      ...row,
      reason: appendNextStep(row.reason),
    };
  });
  return dedupeRecapSkips(rows);
};

export const recapImportBanner = ({
  skipped = [],
  warnings = [],
  importedScores = 0,
} = {}) => {
  if (Number(importedScores) > 0) return null;
  const lines = (skipped || []).map(
    (row) => `${row.label || row.teamCode || "This recap"}: ${row.reason}`
  );
  if ((skipped || []).some((row) => isVerifiedRecapSkip(row))) {
    return {
      type: "error",
      title: "Verified teams were not changed",
      lines: lines.length
        ? lines
        : [
            "Scores on a verified team were left unchanged. Unverify that team on Scoring, then extract again.",
          ],
    };
  }
  const notes = (warnings || []).map((item) => String(item || "").trim()).filter(Boolean);
  return {
    type: "warning",
    title: "No scores imported",
    lines: lines.length
      ? lines
      : notes.length
        ? notes
        : [
            "No scores were imported. Check the photo, confirm the squad, and extract again.",
          ],
  };
};

export const buildRecapExtractFeedback = ({ result, teams } = {}) => {
  const skipped = presentRecapSkips({
    skipped: result?.skipped,
    teams,
  });
  return {
    summary: { ...(result || {}), skipped },
    banner: recapImportBanner({
      skipped,
      warnings: result?.warnings,
      importedScores: result?.importedScores,
    }),
  };
};

export const recapExtractFinishedLabel = ({ run = 1, at } = {}) => {
  const when = at ? new Date(at) : new Date();
  const clock = when.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
  return `Extract finished at ${clock} · run ${Number(run) || 1}`;
};

const sheetNames = (block) => {
  const rows = extractedRows(block);
  if (!rows.length || rows.some((row) => !normalizeName(row.name))) return [];
  return rows.map((row) => normalizeName(row.name));
};

export const resolveRecapTeam = ({
  teams = [],
  extractedCode,
  block,
  verifiedTeamIds = [],
} = {}) => {
  const code = normalizeRecapTeamCode(extractedCode);
  const verified = new Set(verifiedTeamIds);
  const hits = codeCandidates(teams, code);
  const names = sheetNames(block);
  if (hits.length > 1) {
    const fitted = names.length
      ? hits.filter((team) => namesFitRoster(names, rosterNames(team)))
      : [];
    if (fitted.length === 1) return { team: fitted[0], code };
    return {
      error: "This recap matches more than one team.",
      teams: fitted.length ? fitted : hits,
      code,
    };
  }
  if (!hits.length) {
    return { error: "Could not match this recap to a team.", code };
  }
  const team = hits[0];
  const roster = rosterNames(team);
  if (!names.length || namesFitRoster(names, roster)) {
    return { team, code };
  }
  // A full BTA Use with the same number of bowlers is still that card.
  // A misspelled name should not throw the scores away.
  if (recapTeamShortCode(team.id) === code && names.length === roster.length) {
    return { team, code };
  }
  return { error: mismatchReason(team, verified), team, code };
};

const pairBowler = (name, roster, used) => {
  const exact = roster.filter(
    (row) => !used.has(row.id) && normalizeName(row.name) === name
  );
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return null;
  const loose = roster.filter(
    (row) => !used.has(row.id) && looseNameMatch(name, normalizeName(row.name))
  );
  return loose.length === 1 ? loose[0] : null;
};

const matchExtractedBowlers = (team, extractedBowlers) => {
  const roster = rosterBowlers(team);
  const extracted = extractedRows({ bowlers: extractedBowlers });
  if (!roster.length) {
    return { error: "That team has no bowlers." };
  }
  const allNamed =
    extracted.length > 0 && extracted.every((bowler) => normalizeName(bowler.name));
  if (allNamed) {
    const used = new Set();
    const pairs = [];
    let matched = true;
    for (const bowler of extracted) {
      const hit = pairBowler(normalizeName(bowler.name), roster, used);
      if (!hit) {
        matched = false;
        break;
      }
      used.add(hit.id);
      pairs.push({ bowlerId: hit.id, games: bowler.games || [] });
    }
    if (matched) return { pairs };
  }
  if (extracted.length === roster.length) {
    return {
      pairs: extracted.map((bowler, index) => ({
        bowlerId: roster[index].id,
        games: bowler.games || [],
      })),
    };
  }
  if (allNamed) {
    return { error: "Could not match a written name to this team." };
  }
  if (!extracted.length) {
    return { error: "No scores were found for this team." };
  }
  return { error: "Could not tell which bowler a score row belongs to." };
};

const skipIdentity = ({ team, teams, block, code, reason }) => ({
  teamCode: code || (team?.id ? recapTeamShortCode(team.id) : ""),
  teamId: team?.id || "",
  label: team
    ? recapTeamIdentity(team, code)
    : teams?.length
      ? teams.map((item) => recapTeamIdentity(item, code)).join("; ")
      : recapSkipLabel({ block }),
  reason,
});

export const planRecapScoreWrites = ({
  teams = [],
  blocks = [],
  verifiedTeamIds = [],
  gamesByEventId = {},
} = {}) => {
  const verified = new Set(verifiedTeamIds);
  const writes = [];
  const skipped = [];
  const redirects = [];
  const seenTeams = new Set();
  const skipKeys = new Set();

  const pushSkip = (row) => {
    const key = [row.teamId, row.teamCode, row.label, row.reason].join("|");
    if (skipKeys.has(key)) return;
    skipKeys.add(key);
    skipped.push(row);
  };

  (blocks || []).forEach((block) => {
    const code = normalizeRecapTeamCode(block?.teamCode);
    if (!code) {
      pushSkip(
        skipIdentity({
          block,
          reason: "Could not read which team this recap belongs to.",
        })
      );
      return;
    }
    const found = resolveRecapTeam({
      teams,
      extractedCode: code,
      block,
      verifiedTeamIds,
    });
    if (found.error || !found.team?.id) {
      pushSkip(
        skipIdentity({
          team: found.team,
          teams: found.teams,
          block,
          code,
          reason: found.error || "Could not match this recap to a team.",
        })
      );
      return;
    }
    const team = found.team;
    if (verified.has(team.id)) {
      pushSkip(
        skipIdentity({
          team,
          block,
          code: recapTeamShortCode(team.id),
          reason: verifiedRecapReason(team, teams, verified),
        })
      );
      return;
    }
    if (seenTeams.has(team.id)) return;
    const matched = matchExtractedBowlers(team, block.bowlers);
    if (matched.error) {
      pushSkip(
        skipIdentity({
          team,
          block,
          code: recapTeamShortCode(team.id),
          reason: matched.error,
        })
      );
      return;
    }
    const gameCount = Math.max(
      1,
      Number(gamesByEventId[team.eventsID]) || Number(team.gamesPerEvent) || 3
    );
    let wrote = 0;
    matched.pairs.forEach((pair) => {
      for (let game = 1; game <= gameCount; game += 1) {
        const score = parseRecapGameScore(pair.games[game - 1]);
        if (score == null) continue;
        writes.push({
          teamId: team.id,
          bowlerId: pair.bowlerId,
          gameNumber: game,
          score,
        });
        wrote += 1;
      }
    });
    if (!wrote) {
      pushSkip(
        skipIdentity({
          team,
          block,
          code: recapTeamShortCode(team.id),
          reason: "No game scores were read for this team.",
        })
      );
      return;
    }
    seenTeams.add(team.id);
  });

  return {
    writes,
    skipped,
    redirects,
    importedTeamIds: [...seenTeams],
    importedTeams: seenTeams.size,
    importedScores: writes.length,
  };
};
